import {
  type MetacategoryData,
  MetacategoryGroup,
  type MetacategoryGroupType,
} from '@openx/types/targeting/metacategory.ts';
import type { TargetingProps } from '@openx/types/targeting/targeting.ts';
import { Criteria } from '@openx/types/targeting/targetingValuesTypes.ts';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { memo, useEffect, useMemo, useState } from 'react';
import { usePreselectableDrawer } from '../shared/hooks/usePreselectableDrawer.ts';
import { TargetingDrawer } from '../shared/TargetingDrawer/TargetingDrawer.tsx';
import { useDrawer } from '../shared/TargetingDrawer/useDrawer.tsx';
import { MetacategoryItems } from './components/MetacategoryItems/MetacategoryItems.tsx';
import { MetacategoryReadOnly } from './components/MetacategoryReadOnly.tsx';
import { MetacategoryTopBox } from './components/MetacategoryTopBox.tsx';
import { clearMetacategoryCategories, clearMetacategoryKeywords } from './utils/utils.ts';

const emptyMappedTargetingParams: MetacategoryData = {
  exclude_mfa: true,
  excludes: [],
  includes: null,
  inter_dimension_operator: Criteria.ALL,
  keywords: {
    excludes: [],
    includes: [],
  },
};

const getMappedTargetingParams = (targetingParams: MetacategoryData | null): MetacategoryData =>
  cloneDeep({
    ...emptyMappedTargetingParams,
    ...targetingParams,
    keywords: {
      ...emptyMappedTargetingParams.keywords,
      ...(targetingParams?.keywords || {}),
    },
  });

export const Metacategories = memo(function Metacategory({
  targetingParams,
  onFieldUpdate,
  name,
  readonly,
  field,
}: TargetingProps<MetacategoryData>) {
  const mappedTargetingParams = useMemo(() => getMappedTargetingParams(targetingParams), [targetingParams]);
  const [params, updateParams] = useState<MetacategoryData>(getMappedTargetingParams(targetingParams));

  const areParamsNotEmpty = !isEqual(params, emptyMappedTargetingParams);
  const isDataUpdated = !isEqual(mappedTargetingParams, params);

  const { isOpen, onDrawerClose, onDrawerOpen, onDrawerApply } = useDrawer({
    onApply: () => onFieldUpdate?.(field, areParamsNotEmpty ? params : null),
    restoreParams: () => updateParams(mappedTargetingParams),
  });

  const [categoryType, setCategoryType] = useState<MetacategoryGroupType>(
    MetacategoryGroup.ADDITIONAL_OPENX_CATEGORIES
  );

  const isValidFormat = (type: string): type is MetacategoryGroupType => {
    return Object.values(MetacategoryGroup).includes(type as MetacategoryGroupType);
  };

  const openDrawerWithPreselectedType = usePreselectableDrawer<MetacategoryGroupType>(
    isValidFormat,
    onDrawerOpen,
    setCategoryType
  );

  useEffect(() => {
    if (mappedTargetingParams.exclude_mfa !== params.exclude_mfa) {
      updateParams(prevParams => ({ ...prevParams, exclude_mfa: mappedTargetingParams.exclude_mfa }));
    }
  }, [params.exclude_mfa, mappedTargetingParams?.exclude_mfa]);

  const onRemoveCategories = () => {
    const next = clearMetacategoryCategories(emptyMappedTargetingParams, mappedTargetingParams);

    onFieldUpdate?.(field, next);
    updateParams(next);
  };

  const onRemoveKeywords = () => {
    const next = clearMetacategoryKeywords(mappedTargetingParams);

    onFieldUpdate?.(field, next);
    updateParams(next);
  };

  return (
    <>
      <MetacategoryReadOnly
        name={name}
        readonly={readonly}
        targetingParams={targetingParams}
        onOpenDrawerClick={openDrawerWithPreselectedType}
        onRemoveClick={() => {
          onFieldUpdate?.(field, { ...emptyMappedTargetingParams, exclude_mfa: params.exclude_mfa });
          updateParams({ ...emptyMappedTargetingParams, exclude_mfa: params.exclude_mfa });
        }}
        field={field}
        onRemoveCategories={onRemoveCategories}
        onRemoveKeywords={onRemoveKeywords}
      />
      {isOpen && (
        <TargetingDrawer name={name} onClose={onDrawerClose} onApply={onDrawerApply} isApplyDisabled={!isDataUpdated}>
          <MetacategoryTopBox
            items={params}
            onChange={metacategories => updateParams(metacategories)}
            categoryType={categoryType}
            setCategoryType={setCategoryType}
          />
          <MetacategoryItems targetingParams={params} readonly={false} onChange={updateParams} />
        </TargetingDrawer>
      )}
    </>
  );
});
